/**
 * errors.ts — typed failure modes + resilience primitives for the Fusion Engine.
 *
 * Providers talk to unreliable networks; the engine must handle timeouts, rate
 * limits, invalid responses and outright failure without ever letting a broken
 * card reach the user. These primitives turn messy provider errors into a small,
 * classifiable set the engine's fallback chain can reason about.
 */

export class FusionError extends Error {
  constructor(message: string, public readonly providerId?: string) {
    super(message);
    this.name = new.target.name;
  }
}

/** The provider took too long. Transient — safe to retry / fall through. */
export class TimeoutError extends FusionError {}

/** The provider is rate-limited. Do NOT hammer it — fall through immediately. */
export class RateLimitError extends FusionError {
  constructor(message: string, providerId?: string, public readonly retryAfterMs?: number) {
    super(message, providerId);
  }
}

/** The provider returned data that failed validation. */
export class InvalidResponseError extends FusionError {}

/** Network-level failure (DNS, connection reset, offline). Transient. */
export class NetworkError extends FusionError {}

/** No usable provider for a domain. */
export class ProviderUnavailableError extends FusionError {}

/** Errors that are worth retrying (rate limits are NOT — back off instead). */
export function isTransient(err: unknown): boolean {
  return err instanceof TimeoutError || err instanceof NetworkError;
}

export function sleep(ms: number): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Reject if `promise` does not settle within `ms`. */
export function withTimeout<T>(promise: Promise<T>, ms: number, providerId?: string): Promise<T> {
  if (ms <= 0) return promise;
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new TimeoutError(`Provider "${providerId ?? 'unknown'}" timed out after ${ms}ms`, providerId)),
      ms,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export interface RetryOptions {
  /** Number of RE-tries after the first attempt (0 = no retry). */
  retries: number;
  /** Base backoff in ms; grows linearly per attempt. */
  backoffMs: number;
}

/**
 * Run `fn`, retrying transient failures up to `retries` times. Rate limits and
 * invalid responses are NOT retried — they fail fast so the fallback chain can
 * move to the next provider.
 */
export async function withRetry<T>(fn: () => Promise<T>, opts: RetryOptions): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= opts.retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isTransient(err) || attempt === opts.retries) throw err;
      await sleep(opts.backoffMs * (attempt + 1));
    }
  }
  throw lastErr;
}
